import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentResult() {
  const [params] = useSearchParams();
  const txn = params.get("txn");
  const navigate = useNavigate();
  const [state, setState] = useState("CHECKING");
  const [details, setDetails] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!txn) { setState("REJECTED"); return; }
    const check = async () => {
      try {
        const r = await api.get(`/phonepe/verify/${txn}`);
        setDetails(r.data);
        if (r.data.status === "VERIFIED") { setState("VERIFIED"); return; }
        if (r.data.status === "REJECTED") { setState("REJECTED"); return; }
        attemptsRef.current += 1;
        if (attemptsRef.current < 15) setTimeout(check, 3000);
        else setState("TIMEOUT");
      } catch (_e) {
        attemptsRef.current += 1;
        if (attemptsRef.current < 10) setTimeout(check, 3000);
        else setState("REJECTED");
      }
    };
    check();
  }, [txn]);

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <main className="max-w-xl mx-auto px-6 py-16 text-center">
        {state === "CHECKING" && (
          <div data-testid="payment-result-checking">
            <Loader2 className="w-12 h-12 mx-auto text-teal-600 animate-spin" />
            <h1 className="font-heading text-2xl font-extrabold mt-4">Confirming your payment...</h1>
            <p className="text-slate-500 mt-2">This usually takes just a few seconds.</p>
            <p className="font-mono text-xs text-slate-400 mt-4">Txn: {txn}</p>
          </div>
        )}
        {state === "VERIFIED" && (
          <div data-testid="payment-result-verified">
            <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-12 h-12 text-emerald-600" />
            </div>
            <h1 className="font-heading text-3xl font-extrabold mt-4 text-slate-900">Subscription Activated!</h1>
            <p className="text-slate-600 mt-2">Your <strong>{details?.plan}</strong> plan is now active. Enjoy unlimited chat & full directory access.</p>
            <Button onClick={() => navigate("/dashboard")} className="mt-6 bg-teal-600 hover:bg-teal-700 text-white rounded-full px-6">
              Go to dashboard
            </Button>
          </div>
        )}
        {state === "REJECTED" && (
          <div data-testid="payment-result-rejected">
            <div className="w-20 h-20 mx-auto rounded-full bg-rose-100 flex items-center justify-center">
              <XCircle className="w-12 h-12 text-rose-600" />
            </div>
            <h1 className="font-heading text-3xl font-extrabold mt-4 text-slate-900">Payment Failed</h1>
            <p className="text-slate-600 mt-2">{details?.code ? `Reason: ${details.code}` : "Your payment could not be verified."} You can try again.</p>
            <Button onClick={() => navigate("/plans")} className="mt-6 bg-teal-600 hover:bg-teal-700 text-white rounded-full px-6">
              Try again
            </Button>
          </div>
        )}
        {state === "TIMEOUT" && (
          <div data-testid="payment-result-timeout">
            <Loader2 className="w-12 h-12 mx-auto text-amber-600" />
            <h1 className="font-heading text-2xl font-extrabold mt-4">Still confirming...</h1>
            <p className="text-slate-600 mt-2">Your bank is taking longer than usual. Check your dashboard in a minute — the subscription will activate automatically if the payment succeeded.</p>
            <Button onClick={() => navigate("/dashboard")} className="mt-6 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6">
              Go to dashboard
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
